import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { FilterModel } from 'ag-grid-enterprise';
import { Subscription, forkJoin } from 'rxjs';
import { User } from 'src/app/classes/models';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { WidgetPanelCounts } from 'src/app/interfaces/widget-panel-counts';
import { CompanyService } from 'src/app/services/company.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';

// Consider creating new widget for Client managers.
@Component({
  selector: 'app-quick-source-widget',
  templateUrl: './quick-source-widget.component.html',
  styleUrls: ['./quick-source-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class QuickSourceWidgetComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private router: Router,
    private CompanyService: CompanyService,
    private ToastService: ToastService,
    private SharedDataService: SharedDataService
  ) {}

  @Input() CompanyId!: number;

  qsbCounts: Map<
    string,
    {
      count: number;
      query?: {} | undefined;
      route?: string | undefined;
      FilterModel?: FilterModel;
    }
  > = new Map([]);

  showWidget: boolean = false;

  widgetPanelCounts: WidgetPanelCounts = {
    title: 'QuickSourceBids',
    noDataMessage: 'You do not have any proposals',
    hasData: false,
    data: this.qsbCounts
  };

  userAccountRole?: UserAccountRoles;
  SharedDataServiceSubscription?: Subscription;

  navigateToQuickSourceBidsPage() {
    this.router.navigate(['client', this.CompanyId, 'proposal-requests']);
  }

  private loadQuickSourceBids(companyId: number) {
    const quickSourceBidsRoute = `client/${companyId}/proposal-requests`;

    const draftProposalsQuery = {
      status: 'CREATED'
    };

    const releasedProposalsQuery = {
      status: 'RELEASED'
    };

    const closedProposalsQuery = {
      status: 'CLOSED'
    };

    const cancelledProposalsQuery = {
      status: 'CANCELLED'
    };

    const awardedProposalsQuery = {
      status: 'AWARDED'
    };

    forkJoin([
      this.CompanyService.getProposals(companyId, draftProposalsQuery),
      this.CompanyService.getProposals(companyId, releasedProposalsQuery),
      this.CompanyService.getProposals(companyId, closedProposalsQuery),
      this.CompanyService.getProposals(companyId, cancelledProposalsQuery),
      this.CompanyService.getProposals(companyId, awardedProposalsQuery)
    ]).subscribe({
      next: ([
        draftProposals,
        releasedProposals,
        closedProposals,
        cancelledProposals,
        awardedProposals
      ]) => {
        this.qsbCounts.set('Draft Proposals', {
          count: Number(draftProposals.length),
          route: quickSourceBidsRoute,
          FilterModel: {
            requestStatus: {
              filterType: 'text',
              filter: 'CREATED',
              type: 'equals'
            }
          }
        });
        this.qsbCounts.set('Released Proposals', {
          count: Number(releasedProposals.length),
          route: quickSourceBidsRoute,
          FilterModel: {
            requestStatus: {
              filterType: 'text',
              filter: 'RELEASED',
              type: 'equals'
            }
          }
        });
        this.qsbCounts.set('Closed Proposals', {
          count: Number(closedProposals.length),
          route: quickSourceBidsRoute,
          FilterModel: {
            requestStatus: {
              filterType: 'text',
              filter: 'CLOSED',
              type: 'equals'
            }
          }
        });
        this.qsbCounts.set('Cancelled Proposals', {
          count: Number(cancelledProposals.length),
          route: quickSourceBidsRoute,
          FilterModel: {
            requestStatus: {
              filterType: 'text',
              filter: 'CANCELLED',
              type: 'equals'
            }
          }
        });
        this.qsbCounts.set('Awarded Proposals', {
          count: Number(awardedProposals.length),
          route: quickSourceBidsRoute,
          FilterModel: {
            requestStatus: {
              filterType: 'text',
              filter: 'AWARDED',
              type: 'equals'
            }
          }
        });

        const totalProposals =
          draftProposals.length +
          releasedProposals.length +
          closedProposals.length +
          cancelledProposals.length +
          awardedProposals.length;

        this.qsbCounts.set('Total Proposals', totalProposals);
        this._cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.SharedDataServiceSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent<User>().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetLoggedInUser' && data) {
            if (!data) {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error retrieving user information.',
                { returnError: true }
              );
            }

            const { UserAccountRole } = data;

            if (!UserAccountRole) {
              throw this.ToastService.addErrorToast(
                'Error',
                'User is missing permission information.',
                { returnError: true }
              );
            }

            const { role } = UserAccountRole;

            if (!role) {
              throw this.ToastService.addErrorToast(
                'Error',
                'User is missing permission.',
                { returnError: true }
              );
            }

            this.userAccountRole = role;

            if (role === 'CLIENT_ADMIN') {
              this.loadQuickSourceBids(this.CompanyId);
            }
            this._cdr.markForCheck();
          }
        }
      });

    this.SharedDataService.sendEvent('GetLoggedInUser');
  }
}
